import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { Configuration } from 'src/api/microservices/platform-markets-stonex-config'
import {
  DeleteDatasourceConfiguration,
  FindDatasourceConfiguration,
  GetDatasourceConfigurables,
  GetDefaultDatasourceConfiguration,
  ShowDatasourceWebhookConfiguration,
  UpdateOrCreateDatasourceConfiguration,
} from 'src/api/microservices/platform-markets-stonex-config'
import { useCompanyContext } from 'src/companies/routes/company-context'

export const PLATFORM_MARKETS_STONEX_CONFIGURABLES_QUERY_KEY =
  'platform-markets-stonex-configurables'
export const PLATFORM_MARKETS_STONEX_DATASOURCE_CONFIGURATION_QUERY_KEY =
  'platform-markets-stonex-datasource-configuration'
export const PLATFORM_MARKETS_STONEX_DEFAULT_DATASOURCE_CONFIGURATION_QUERY_KEY =
  'platform-markets-stonex-default-datasource-configuration'
export const PLATFORM_MARKETS_STONEX_DATASOURCE_WEBHOOK_CONFIGURATION_QUERY_KEY =
  'platform-markets-stonex-datasource-webhook-configuration'
export const PLATFORM_MARKETS_STONEX_UPDATE_OR_CREATE_DATASOURCE_CONFIGURATION_MUTATION_KEY =
  'platform-markets-stonex-update-or-create-datasource-configuration'
export const PLATFORM_MARKETS_STONEX_DELETE_DATASOURCE_CONFIGURATION_MUTATION_KEY =
  'platform-markets-stonex-delete-datasource-configuration'

export function useGetDatasourceConfigurables() {
  const { company } = useCompanyContext()
  return useQuery<TenantConfigResponse[]>({
    queryKey: [PLATFORM_MARKETS_STONEX_CONFIGURABLES_QUERY_KEY, { tenantId: company.tenant_id }],
    queryFn: async () => {
      const [err, response] = await GetDatasourceConfigurables(company.tenant_id)
      if (err) throw err
      return response.configurables
    },
  })
}

export function useGetDefaultDatasourceConfiguration(enabled = true) {
  const {
    company: { tenant_id: tenantId },
  } = useCompanyContext()

  return useQuery<Configuration[]>({
    queryKey: [PLATFORM_MARKETS_STONEX_DEFAULT_DATASOURCE_CONFIGURATION_QUERY_KEY, { tenantId }],
    queryFn: async () => {
      const [err, response] = await GetDefaultDatasourceConfiguration(tenantId)
      if (err) throw err
      return response.configurations
    },
    enabled: !!tenantId && enabled,
  })
}

export function useFindDatasourceConfiguration() {
  const {
    company: { tenant_id: tenantId, datasource_id: datasourceId },
  } = useCompanyContext()

  return useQuery<Configuration[] | null>({
    queryKey: [
      PLATFORM_MARKETS_STONEX_DATASOURCE_CONFIGURATION_QUERY_KEY,
      { tenantId, datasourceId },
    ],
    queryFn: async () => {
      const [err, response] = await FindDatasourceConfiguration({ id: datasourceId }, tenantId)
      if (err) {
        if (err?.response?.status === 404) return null
        throw err
      }
      return response.configurations
    },
    enabled: !!datasourceId,
    retry: false,
  })
}

export function useShowDatasourceWebhookConfiguration(enabled = true) {
  const {
    company: { tenant_id: tenantId, datasource_id: datasourceId },
  } = useCompanyContext()

  return useQuery<{ webhookSecret: string; webhookUrl: string }>({
    queryKey: [
      PLATFORM_MARKETS_STONEX_DATASOURCE_WEBHOOK_CONFIGURATION_QUERY_KEY,
      { tenantId, datasourceId },
    ],
    queryFn: async () => {
      const [err, response] = await ShowDatasourceWebhookConfiguration(
        { id: datasourceId },
        tenantId
      )
      if (err) throw err
      return response
    },
    enabled: !!datasourceId && enabled,
    retry: false,
  })
}

function normalizeStoneXConfigurationValue(value: any) {
  if (value == null || value === 'undefined') return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value).trim()
}

export function useUpdateOrCreateDatasourceConfiguration() {
  const {
    company: { tenant_id: tenantId, datasource_id: datasourceId },
  } = useCompanyContext()

  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [
      PLATFORM_MARKETS_STONEX_UPDATE_OR_CREATE_DATASOURCE_CONFIGURATION_MUTATION_KEY,
      { tenantId, datasourceId },
    ],
    mutationFn: async (configurations: Array<Configuration>) => {
      const [err, response] = await UpdateOrCreateDatasourceConfiguration(
        {
          datasourceId,
          configurations: configurations.map(({ key, value }) => ({
            key,
            value: normalizeStoneXConfigurationValue(value),
          })),
        },
        tenantId
      )
      if (err) throw err

      await queryClient.invalidateQueries({
        queryKey: [PLATFORM_MARKETS_STONEX_DATASOURCE_CONFIGURATION_QUERY_KEY],
      })

      return response.configurations
    },
  })
}

export function useDeleteDatasourceConfiguration() {
  const {
    company: { tenant_id: tenantId, datasource_id: datasourceId },
  } = useCompanyContext()

  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: [
      PLATFORM_MARKETS_STONEX_DELETE_DATASOURCE_CONFIGURATION_MUTATION_KEY,
      { tenantId, datasourceId },
    ],
    mutationFn: async () => {
      const [err, response] = await DeleteDatasourceConfiguration({ id: datasourceId }, tenantId)
      if (err) throw err

      await queryClient.invalidateQueries({
        queryKey: [PLATFORM_MARKETS_STONEX_DATASOURCE_CONFIGURATION_QUERY_KEY],
      })
      await queryClient.invalidateQueries({
        queryKey: [PLATFORM_MARKETS_STONEX_DATASOURCE_WEBHOOK_CONFIGURATION_QUERY_KEY],
      })

      return response
    },
  })
}
